import { useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import CopiesInput from "@/components/CopiesInput";
import { buildKapeiRecommendations } from "@/lib/kapeiBusinessRules";

const fallbackPaperOptions = [
  { value: "normal", label: "Normal" },
  { value: "cartulina", label: "Cartulina" },
  { value: "fotografico", label: "Fotográfico" },
  { value: "pegatina", label: "Pegatina" },
  { value: "otro", label: "Otro" },
];

const finishFilters = {
  stapling: (item) => item.slug === "grapado",
  binding: (item) => item.category === "encuadernacion",
  lamination: (item) => item.category === "plastificado",
  perforation: (item) => item.slug === "perforado-taladro",
  cutting: (item) => item.slug === "corte",
  folding: (item) => item.slug === "doblado",
  foam_board: (item) => item.slug?.includes("carton-pluma") || item.category === "soportes",
  engraving: (item) => item.slug?.includes("grabado"),
};

const finishLabels = {
  stapling: "Grapado",
  binding: "Encuadernación",
  lamination: "Plastificado",
  perforation: "Perforado",
  cutting: "Corte",
  folding: "Doblado",
  foam_board: "Cartón pluma",
  engraving: "Grabado",
};

const optionRow = (label, name, opts, value, setValue) => (
  <div>
    <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">{label}</Label>
    <RadioGroup value={value} onValueChange={setValue} className="mt-2 grid grid-cols-2 gap-2">
      {opts.map((o) => (
        <label
          key={o.value}
          data-testid={`opt-${name}-${o.value}`}
          className={`flex cursor-pointer items-center gap-2 rounded-xl border-2 p-3 text-base font-bold ${
            value === o.value
              ? "border-[#4d7c0f] bg-[#4d7c0f]/5 text-[#4d7c0f]"
              : "border-slate-200 text-slate-700"
          }`}
        >
          <RadioGroupItem value={o.value} className="sr-only" />
          {o.label}
        </label>
      ))}
    </RadioGroup>
  </div>
);

function catalogOptions(catalog, filterFn, fallback = []) {
  const items = (catalog || [])
    .filter((item) => item.visible_client !== false || item.status === "active" || item.status === "staff_only")
    .filter(filterFn)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((item) => ({
      value: item.slug || item.id || item.name,
      label: `${item.icon || ""} ${item.name || item.slug}`.trim(),
    }));

  return items.length ? items : fallback;
}

export default function DynamicConfigurator({
  opts,
  setOpts,
  sizeOther,
  setSizeOther,
  paperOther,
  setPaperOther,
  catalog = [],
  service = null,
  entries = [],
}) {
  const enabledOptions = useMemo(() => {
    const raw = Array.isArray(service?.options) ? service.options : [];
    return raw
      .filter((o) => o.enabled !== false)
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  }, [service]);

  const hasCatalogOptions = enabledOptions.length > 0;
  const shouldShow = (key) => !hasCatalogOptions || enabledOptions.some((o) => o.key === key);

  const paperOptions = useMemo(() => {
    const materials = catalogOptions(
      catalog,
      (item) => {
        const isMaterial =
          item.category === "papel" ||
          item.category === "pegatinas" ||
          item.service_type === "material";

        if (!isMaterial) return false;

        const compat = Array.isArray(item.compatible_with)
          ? item.compatible_with
          : [];

        if (!compat.length) return true;

        return compat.includes(service?.slug);
      },
      fallbackPaperOptions
    );

    const hasOther = materials.some((x) => x.value === "otro");
    return hasOther ? materials : [...materials, { value: "otro", label: "Otro" }];
  }, [catalog, service?.slug]);

  const getFinishOptions = (key) => {
    const filter = finishFilters[key];
    if (!filter) return [];
    return catalogOptions(
      catalog,
      (item) =>
        (item.service_type === "finish" ||
          ["acabados", "plastificado", "encuadernacion", "soportes"].includes(item.category)) &&
        filter(item),
      []
    );
  };

  const setSidesManual = (v) => {
    setOpts({ ...opts, sides: v });
  };

  const recommendations = useMemo(
    () => buildKapeiRecommendations({ entries, opts }),
    [entries, opts]
  );

  useEffect(() => {
    const auto = recommendations.find((r) => r.action?.type === "set_option");
    if (!auto) return;
    const { field, value } = auto.action;
    if (opts[field] === value) return;
    setOpts({ ...opts, [field]: value });
  }, [recommendations, opts, setOpts]);

  const toggleFinish = (slug) => {
    const current = Array.isArray(opts.finishes) ? opts.finishes : [];
    const next = current.includes(slug)
      ? current.filter((x) => x !== slug)
      : [...current, slug];
    setOpts({ ...opts, finishes: next });
  };

  const renderFinishGroup = (key) => {
    if (!shouldShow(key)) return null;
    const options = getFinishOptions(key);
    if (!options.length) return null;

    return (
      <div key={key}>
        <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">
          {finishLabels[key] || "Acabado"}
        </Label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {options.map((o) => {
            const checked = (opts.finishes || []).includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                data-testid={`opt-finish-${o.value}`}
                onClick={() => toggleFinish(o.value)}
                className={`rounded-xl border-2 p-3 text-left text-sm font-black ${
                  checked
                    ? "border-[#4d7c0f] bg-[#4d7c0f]/5 text-[#4d7c0f]"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border-2 border-slate-200 bg-slate-50 p-4">
      {shouldShow("copies") && (
      <div>
        <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">Copias</Label>
        <div className="mt-2">
          <CopiesInput
            value={opts.copies}
            onChange={(n) => setOpts({ ...opts, copies: n })}
            testid="opt-copies"
          />
        </div>
      </div>
      )}

      {shouldShow("color") && optionRow(
        "Tinta",
        "color",
        [
          { value: "bw", label: "Blanco y negro" },
          { value: "color", label: "Color" },
        ],
        opts.color,
        (v) => setOpts({ ...opts, color: v })
      )}

      {shouldShow("sides") && (
      <div>
        <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">Caras</Label>
        <RadioGroup value={opts.sides} onValueChange={setSidesManual} className="mt-2 grid grid-cols-2 gap-2">
          {[
            { value: "single", label: "Una cara" },
            { value: "double", label: "Dos caras" },
          ].map((o) => (
            <label
              key={o.value}
              data-testid={`opt-sides-${o.value}`}
              className={`flex cursor-pointer items-center gap-2 rounded-xl border-2 p-3 text-base font-bold ${
                opts.sides === o.value
                  ? "border-[#4d7c0f] bg-[#4d7c0f]/5 text-[#4d7c0f]"
                  : "border-slate-200 text-slate-700"
              }`}
            >
              <RadioGroupItem value={o.value} className="sr-only" />
              {o.label}
            </label>
          ))}
        </RadioGroup>
      </div>
      )}

      {shouldShow("size") && (
      <div>
        {optionRow(
          "Tamaño",
          "size",
          [
            { value: "A4", label: "A4" },
            { value: "A3", label: "A3" },
            { value: "Otro", label: "Otro" },
          ],
          opts.size,
          (v) => setOpts({ ...opts, size: v })
        )}
        {opts.size === "Otro" && (
          <Input
            data-testid="opt-size-other"
            value={sizeOther}
            onChange={(e) => setSizeOther(e.target.value)}
            placeholder="Indícanos el tamaño (ej: A5, 50x70 cm…)"
            className="mt-2 h-14 rounded-xl border-2 border-slate-300 px-4 text-base"
          />
        )}
      </div>
      )}

      {shouldShow("paper") && (
      <div>
        {optionRow("Papel", "paper", paperOptions, opts.paper, (v) => setOpts({ ...opts, paper: v }))}
        {opts.paper === "otro" && (
          <Input
            data-testid="opt-paper-other"
            value={paperOther}
            onChange={(e) => setPaperOther(e.target.value)}
            placeholder="Indícanos el tipo de papel (gramaje, acabado…)"
            className="mt-2 h-14 rounded-xl border-2 border-slate-300 px-4 text-base"
          />
        )}
      </div>
      )}

      {["stapling", "binding", "lamination", "perforation", "cutting", "folding", "foam_board", "engraving"].map(renderFinishGroup)}
      {recommendations.filter((r) => r.type !== "auto").length > 0 && (
        <div className="space-y-2">
          {recommendations.filter((r) => r.type !== "auto").map((r) => (
            <div
              key={r.key}
              className={`rounded-2xl border-2 p-3 text-sm ${
                r.type === "warning"
                  ? "border-amber-300 bg-amber-50 text-amber-950"
                  : "border-[#4d7c0f]/30 bg-[#4d7c0f]/5 text-[#365314]"
              }`}
            >
              <div className="font-black">{r.title}</div>
              <div className="mt-1 font-semibold">{r.message}</div>
            </div>
          ))}
        </div>
      )}

      )}
    </div>
  );
}
